import { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

const MIN = 1;
const MAX = 5;

export default function Lightbox({ images, index, onClose, onNavigate }) {
  const [scale, setScale] = useState(MIN);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const rootRef = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const drag = useRef(null);
  const lastTap = useRef(0);
  const triggerRef = useRef(null);

  const zoomed = scale > MIN + 0.01;
  const img = images[index];

  const clampPos = (p, s) => {
    const el = rootRef.current;
    if (!el) return p;
    const mx = (el.clientWidth * (s - 1)) / 2;
    const my = (el.clientHeight * (s - 1)) / 2;
    return { x: Math.max(-mx, Math.min(mx, p.x)), y: Math.max(-my, Math.min(my, p.y)) };
  };

  const applyScale = (s) => {
    const ns = Math.max(MIN, Math.min(MAX, s));
    setScale(ns);
    setPos((p) => (ns === MIN ? { x: 0, y: 0 } : clampPos(p, ns)));
  };

  const reset = () => {
    setScale(MIN);
    setPos({ x: 0, y: 0 });
  };

  const go = useCallback(
    (dir) => {
      reset();
      onNavigate((index + dir + images.length) % images.length);
    },
    [index, images.length, onNavigate]
  );

  useEffect(() => { reset(); }, [index]);

  useEffect(() => {
    triggerRef.current = document.activeElement;
    const savedScroll = window.scrollY;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    requestAnimationFrame(() => {
      if (window.__lenis) window.__lenis.scrollTo(savedScroll, { immediate: true, force: true });
      else window.scrollTo(0, savedScroll);
    });
    rootRef.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
      if (triggerRef.current && triggerRef.current.focus) triggerRef.current.focus({ preventScroll: true });
      requestAnimationFrame(() => {
        if (window.__lenis) window.__lenis.scrollTo(savedScroll, { immediate: true, force: true });
        else window.scrollTo(0, savedScroll);
      });
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const btns = rootRef.current?.querySelectorAll("button");
        if (!btns || !btns.length) return;
        const first = btns[0];
        const last = btns[btns.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (zoomed) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        go(1);
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        go(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, zoomed, onClose]);

  const toggleZoom = () => applyScale(zoomed ? MIN : 2.5);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale };
      drag.current = null;
    } else if (pointers.current.size === 1) {
      drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current.dist > 0) applyScale(pinch.current.scale * (d / pinch.current.dist));
    } else if (drag.current && zoomed) {
      setPos(clampPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }, scale));
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        toggleZoom();
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
      drag.current = null;
    }
  };

  if (!img) return null;

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      data-testid="lightbox"
      className="fixed inset-0 z-[80] bg-ink/95 backdrop-blur-sm outline-none select-none"
      onClick={(e) => {
        if (e.target === e.currentTarget && !zoomed) onClose();
      }}
    >
      <div className="absolute top-5 left-5 md:top-6 md:left-6 z-20 font-mono text-xs tracking-[0.25em] text-paper/50" data-testid="lightbox-count">
        {index + 1} / {images.length}
      </div>
      <button
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label="Close viewer"
        className="absolute top-4 right-4 z-20 p-3 text-paper/80 hover:text-brand transition-colors"
      >
        <X size={26} strokeWidth={1.5} />
      </button>

      {!zoomed && images.length > 1 && (
        <>
          <button
            onClick={() => go(-1)}
            data-testid="lightbox-prev"
            aria-label="Previous image"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-20 p-3 md:p-4 text-paper/70 hover:text-brand transition-colors"
          >
            <ChevronLeft size={32} strokeWidth={1.5} />
          </button>
          <button
            onClick={() => go(1)}
            data-testid="lightbox-next"
            aria-label="Next image"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-20 p-3 md:p-4 text-paper/70 hover:text-brand transition-colors"
          >
            <ChevronRight size={32} strokeWidth={1.5} />
          </button>
        </>
      )}

      <div
        className="absolute inset-0 flex items-center justify-center p-4 md:p-14 overflow-hidden"
        style={{ touchAction: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={toggleZoom}
      >
        <img
          src={img.url}
          alt={img.alt || img.caption || `Image ${index + 1}`}
          draggable={false}
          data-testid="lightbox-image"
          className="max-w-full max-h-full object-contain"
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
            cursor: zoomed ? "grab" : "zoom-in",
            transition: drag.current || pinch.current ? "none" : "transform 0.18s ease-out",
          }}
        />
      </div>

      {img.caption && (
        <p
          data-testid="lightbox-caption"
          className="absolute bottom-6 left-4 md:left-6 z-10 max-w-[55vw] font-mono text-[11px] uppercase tracking-[0.2em] text-paper/60 leading-relaxed"
        >
          {img.caption}
        </p>
      )}

      <div className="absolute bottom-5 right-4 md:right-6 z-20 flex gap-2">
        <button
          onClick={() => applyScale(scale / 1.5)}
          disabled={scale <= MIN}
          data-testid="lightbox-zoom-out"
          aria-label="Zoom out"
          className="p-3.5 bg-paper/10 hover:bg-brand text-paper transition-colors disabled:opacity-30"
        >
          <ZoomOut size={18} strokeWidth={1.5} />
        </button>
        <button
          onClick={reset}
          data-testid="lightbox-reset"
          aria-label="Reset zoom"
          className="p-3.5 bg-paper/10 hover:bg-brand text-paper transition-colors"
        >
          <RotateCcw size={18} strokeWidth={1.5} />
        </button>
        <button
          onClick={() => applyScale(scale * 1.5)}
          disabled={scale >= MAX}
          data-testid="lightbox-zoom-in"
          aria-label="Zoom in"
          className="p-3.5 bg-paper/10 hover:bg-brand text-paper transition-colors disabled:opacity-30"
        >
          <ZoomIn size={18} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
